import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Affix,
  Input,
  InputNumber,
  Select,
  Button,
  Switch,
  Divider,
  Tooltip,
  Tag,
  Table,
  Pagination,
  Radio,
  Checkbox,
} from "antd";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { RootState } from "../../../app/store";
import SelectLn2Message from "../../components/select/SelectLn2Message";
import Ln2MappingEditor, { Ln2MappingData } from "../../components/adapters/Ln2MappingEditor";
import {
  LN2_MESSAGE_CATEGORIES,
  parseCompositeKey,
  categoryKeyToMessageContent,
  getDisplayName,
} from "../../../flatbuffers/types/ln2MessageOptions";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { findLn1DuplicateIndices, intraContentCategories } from "./StepAdaptersCdoEditor";
import type { FindLn1DuplicateContext } from "./StepAdaptersCdoEditor";
import computeCategoryByteSize from "../../components/adapters/adapterSizeMath";
import {
  categoryOccupancy,
  occupancyCollisions,
  rangesActuallyCollide,
  rowsOccupancy,
} from "../../components/adapters/categoryOccupancy";
import type { CategoryOccupancy } from "../../components/adapters/categoryOccupancy";
import { KeyDirection } from "../../../flatbuffers/generated/archive/key-direction";
import {
  OVERLAP_STYLE,
  isGroupScopedCategory,
} from "../../components/adapters/offsetOverlapDetection";

// ************************************************ */
// TYPES ****************************************** */

export interface Ln1KeyMappingRow {
  /** Backend key id when this row was loaded from the API. undefined for
   *  rows the user has added in the current session. Set on the primary
   *  row only — paired secondary rows share the primary's api record. */
  id?: string;
  ln2MessageKey: string;
  contentType: "bit" | "byte";
  byte_offset: number;
  bit_offset: number;
  data: boolean;
  offset: number;
  value: number;
  units_based: boolean;
  floors_based: boolean;
  /** When true, this mapping's offset is resolved individually at the bank
   *  matrix level instead of sharing the category's base offset. Adapter-level
   *  duplicate checking is skipped for bank_level mappings. Bank-level means
   *  the offset applies to the whole bank (no per-car delta). */
  bank_level: boolean;
  /** When true, this mapping gets its OWN column in the Offset Matrix with
   *  per-car semantics (Initial Offset + Car Delta). Complements bank_level:
   *  bank_level=true → own column, no car delta; individual=true → own column,
   *  with car delta; both false → merged into the grouped category column.
   *  bank_level wins when both are true. */
  individual: boolean;
  /** Treat this mapping's MessageContent category as a group-shared register
   *  (one register shared by every car). Suppresses the false cross-car
   *  overlap in the Offset Matrix. Defaults on for Group Supervisory Status /
   *  Group Control (group-scoped by definition), operator-overridable. */
  group_shared: boolean;
  /** Debounce time in milliseconds for this key. null = no debounce. */
  debounce_time: number | null;
  /** Shared UUID linking two paired rows. null = unpaired. */
  pair_id: string | null;
  /** True if this row is the secondary (active=false) member of a pair. */
  is_pair_secondary: boolean;
  /** Which of the two LN1 maps this key feeds. BOTH (the default) reproduces
   *  the historical behaviour where one key served both directions; TO_DEVICE
   *  and FROM_DEVICE let one wire address carry a different LN2 message when
   *  written than when read. Only editable while the adapter has opted in via
   *  direction_aware_keys.
   *
   *  Optional, and absent reads as BOTH — the same "absent means BOTH" contract
   *  the column (nil), the wire (unset FlatBuffer field) and the server
   *  (DirectionOrDefault) all use. A row deserialized from an API response that
   *  predates the field genuinely has no direction, and forcing callers to
   *  invent one would be a lie about what was received. */
  direction?: KeyDirection;
}

interface CdoLn1MappingEditorProps {
  mappings: Ln1KeyMappingRow[];
  onMappingsChange: (mappings: Ln1KeyMappingRow[]) => void;
  unitOffsets: Partial<Record<MessageContent, number[]>>;
  onUnitOffsetsChange: (offsets: Partial<Record<MessageContent, number[]>>) => void;
  intraContentFlags: Partial<Record<MessageContent, boolean[]>>;
  onIntraContentFlagsChange: (flags: Partial<Record<MessageContent, boolean[]>>) => void;
  units: number;
  floors: number;
  onUnitsChange: (units: number) => void;
  hideOffsetGrid?: boolean;
  hideMappingGrid?: boolean;
  /**
   * When provided, renders an Ln2MappingEditor (bank + lift selectors only,
   * message picker hidden) at the top of this component. Use this when the
   * caller needs the user to select a bank/lift context for the LN1 adapter.
   */
  ln2BankLift?: Ln2MappingData;
  /**
   * Called whenever the bank/lift selection changes inside the embedded
   * Ln2MappingEditor. Required when ln2BankLift is provided.
   */
  onLn2BankLiftChange?: (data: Ln2MappingData) => void;
  /**
   * Per-lift bank offset arrays, keyed by column key. When provided, bank_level
   * rows are checked against the bank matrix for differentiation before flagging.
   */
  bankMatrixPerLift?: ReadonlyArray<Record<string, number>>;
  /**
   * Per-individual-message offsets for rows with individual=true.
   * Composite key → [initial, initial+delta] (same shape as ln1IndividualOffsets
   * in CdoStateBundle). When provided, individual rows can clear their duplicate
   * flag when the adapter matrix differentiates across cars.
   */
  individualOffsets?: Partial<Record<string, [number, number]>>;
  /**
   * When true, the adapter has opted into per-key direction and each row gets a
   * Direction control. Off by default and off for every existing adapter, so
   * the editor looks and behaves exactly as it did before the feature existed.
   */
  directionAware?: boolean;
}

type ViewMode = "normal" | "compact" | "expanded";

/**
 * Normalize a string for Ln2-message substring search: lowercase and strip
 * everything except alphanumerics. Lets "Usage::Up", "usage up", "usage:up"
 * all match the stored key "usage:normal:5" (which resolves to "Usage: Up").
 */
function normalizeLn2Needle(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Walk up the DOM from `el` to find the nearest ancestor whose computed
 * overflow-y is auto or scroll AND whose content actually overflows. Falls
 * back to window when no such ancestor exists. Used to correctly pin an
 * Affix inside a Drawer (scroll is on .ant-drawer-body, not window).
 */
export function findScrollTarget(el: HTMLElement | null): HTMLElement | Window {
  if (typeof window === "undefined") return window;
  let cur: HTMLElement | null = el?.parentElement ?? null;
  while (cur && cur !== document.body && cur !== document.documentElement) {
    const style = window.getComputedStyle(cur);
    const oy = style.overflowY;
    if ((oy === "auto" || oy === "scroll") && cur.scrollHeight > cur.clientHeight) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return window;
}

/**
 * Compute an array insertion index so that a newly-added row lands at the
 * user's current scroll position. Scans rows carrying `attr` inside `rootEl`,
 * returns the index of the first row whose viewport top is at or below the
 * scroll container's top (i.e., the first row about to be visible). If the
 * user has scrolled past every row, returns `fallbackLength` (append).
 */
export function findInsertionIndexAtScroll(
  target: HTMLElement | Window,
  rootEl: HTMLElement | null,
  attr: string,
  fallbackLength: number,
): number {
  if (!rootEl || typeof window === "undefined") return fallbackLength;
  const containerTop = target === window ? 0 : (target as HTMLElement).getBoundingClientRect().top;
  const rows = Array.from(rootEl.querySelectorAll<HTMLElement>(`[${attr}]`));
  const firstVisible = rows.find((row) => row.getBoundingClientRect().top >= containerTop - 1);
  if (firstVisible) {
    const idxStr = firstVisible.getAttribute(attr);
    if (idxStr !== null) {
      const n = Number(idxStr);
      if (Number.isFinite(n)) return n;
    }
  }
  return fallbackLength;
}

// ************************************************ */
// HELPERS **************************************** */

function defaultMapping(): Ln1KeyMappingRow {
  return {
    ln2MessageKey: "",
    contentType: "bit",
    byte_offset: 0,
    bit_offset: 0,
    data: true,
    offset: 0,
    value: 0,
    units_based: true,
    floors_based: false,
    bank_level: false,
    individual: false,
    group_shared: false,
    debounce_time: null,
    pair_id: null,
    is_pair_secondary: false,
    direction: KeyDirection.BOTH,
  };
}

function nextMapping(lastRow: Ln1KeyMappingRow): Ln1KeyMappingRow {
  const base: Ln1KeyMappingRow = {
    ...defaultMapping(),
    contentType: lastRow.contentType,
    units_based: lastRow.units_based,
    floors_based: lastRow.floors_based,
    bank_level: lastRow.bank_level,
    individual: lastRow.individual,
    group_shared: lastRow.group_shared,
    // Carried forward like the other per-row toggles: a user splitting one
    // address usually splits the next one too.
    direction: lastRow.direction,
  };

  if (lastRow.contentType === "bit") {
    let nextBit = lastRow.bit_offset + 1;
    let nextByte = lastRow.byte_offset;
    if (nextBit > 7) {
      nextBit = 0;
      nextByte += 1;
    }
    base.byte_offset = nextByte;
    base.bit_offset = nextBit;
    base.data = lastRow.data;
  } else {
    base.offset = lastRow.offset;
    base.value = lastRow.value + 1;
  }

  return base;
}

const CONTENT_TYPE_OPTIONS = [
  { label: "Bit Message", value: "bit" },
  { label: "Byte Message", value: "byte" },
];

const CONTENT_TYPE_OPTIONS_SHORT = [
  { label: "Bit", value: "bit" },
  { label: "Byte", value: "byte" },
];

const CATEGORY_FILTER_OPTIONS = LN2_MESSAGE_CATEGORIES.map((cat) => ({
  label: cat.label,
  value: cat.categoryKey,
}));

/** Simple string -> integer hash (djb2 variant using multiplication). */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = h * 33 + s.charCodeAt(i);
  }
  return h;
}

/** Deterministic hash -> HSL color for a category key string. */
function categoryColor(categoryKey: string): string {
  const hue = ((hashString(categoryKey) % 360) + 360) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

/** Lighter variant for card borders / backgrounds. */
function categoryBorderColor(categoryKey: string): string {
  const hue = ((hashString(categoryKey) % 360) + 360) % 360;
  return `hsl(${hue}, 50%, 85%)`;
}

/** Resolve border color: red for duplicates, category-based if assigned, default gray. */
function resolveBorderColor(
  isDupe: boolean,
  catKey: string | null,
  useBorderVariant: boolean,
): string {
  if (isDupe) return "#ff4d4f";
  if (catKey) return useBorderVariant ? categoryBorderColor(catKey) : categoryColor(catKey);
  return "#d9d9d9";
}

/** Get the category label for a mapping row, or null if unassigned. */
function getCategoryLabel(ln2MessageKey: string): string | null {
  if (!ln2MessageKey) return null;
  const parsed = parseCompositeKey(ln2MessageKey);
  if (!parsed) return null;
  const cat = LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === parsed.categoryKey);
  return cat?.label ?? null;
}

/** Get the categoryKey for a mapping row, or empty string. */
function getCategoryKey(ln2MessageKey: string): string {
  if (!ln2MessageKey) return "";
  const parsed = parseCompositeKey(ln2MessageKey);
  return parsed?.categoryKey ?? "";
}

/** Get the label for a categoryKey. */
function labelForCategoryKey(catKey: string): string {
  const cat = LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === catKey);
  return cat?.label ?? catKey;
}

// ************************************************ */
// OVERLAP DETECTION ****************************** */

/** A byte range [start, end) with identifying labels. */
interface ByteRange {
  start: number;
  end: number;
  msgContent: MessageContent;
  unitIndex: number;
  /** The matrix column the range belongs to: the category, as `String(mc)`, or
   *  the composite key of a message broken out into its own column. */
  column: string;
  /** The bytes the category's messages actually claim inside [start, end).
   *  A range is a bounding box; this is what is really in it. */
  occupancy: CategoryOccupancy;
}

/**
 * A category-level conflict, resolved down to the two messages responsible.
 *
 * The offset matrix paints categories, because a category is what carries an
 * offset. The mapping list paints messages, because a message is what the
 * operator moves to fix the conflict — and being told "Status overlaps Traffic"
 * while looking at thirty Status rows names the problem without locating it.
 */
export interface Ln1CategoryConflict {
  unitIndex: number;
  absByte: number;
  aRowIndex: number;
  bRowIndex: number;
  aMsgContent: MessageContent;
  bMsgContent: MessageContent;
}

/** Everything one detection run found, at both granularities. */
export interface Ln1OverlapResult {
  /** "MessageContent:unitIndex" for each grouped offset-matrix cell to paint, or
   *  "<composite key>:unitIndex" for a message broken out into its own column. */
  cells: Set<string>;
  /** Mapping-row indices that take part in a cross-category collision. */
  rowIndices: Set<number>;
  /** Every colliding pair, for tooltips. */
  conflicts: Ln1CategoryConflict[];
}

/**
 * Compute all overlapping (MessageContent, unitIndex) pairs across ALL units.
 * Checks both intra-unit (two categories in the same unit) and inter-unit
 * (a category in one unit bleeding into another unit's range).
 *
 * When two ranges overlap at the SAME unit and BOTH have intra-content
 * flags set, the overlap is allowed (shared register).
 *
 * A range overlap is the GATE, not the verdict. A category's range is the
 * bounding box of its messages, so two categories can share bytes that neither
 * of them puts a register on: Status at 100 spanning ten bytes and Traffic at
 * 105 spanning ten collide on 105-109 only if Status really has a message at its
 * relative byte 5..9 AND Traffic one at its relative 0..4. Comparing the boxes
 * alone reported the pair red either way, so an operator who had already laid
 * the two categories out so their registers interleave was told to move an
 * offset that was correct — and, seeing that, learned to disregard the red.
 *
 * `categoryOccupancy` places every message on its absolute byte and
 * `rangesActuallyCollide` intersects the two sets, so the pair survives only
 * when some byte is claimed twice. This can only ever excuse: a category whose
 * messages fill its range behaves exactly as it did, and a category with no
 * mappings to describe it is not excused at all, because then the range is the
 * only evidence there is.
 */
export function detectLn1Overlaps(
  mappings: Ln1KeyMappingRow[],
  unitOffsets: Partial<Record<MessageContent, number[]>>,
  units: number,
  floors: number,
  intraContentFlags: Partial<Record<MessageContent, boolean[]>> = {},
  individualOffsets: Partial<Record<string, [number, number]>> = {},
): Ln1OverlapResult {
  // A message with its own matrix column sits where that column puts it --
  // `initial + car * delta` -- not inside its category's grouped range. Placing
  // it in the grouped range instead stacks every broken-out column of a
  // category that has no grouped offset at byte 0, where they "collide" with
  // each other and with every other such category on every car.
  const ownColumn = (row: Ln1KeyMappingRow): boolean =>
    individualOffsets[row.ln2MessageKey] !== undefined;

  // Collect unique units_based MessageContent values
  const msgContents = new Set<MessageContent>();
  mappings.forEach((m) => {
    if (!m.units_based || !m.ln2MessageKey) return;
    const parsed = parseCompositeKey(m.ln2MessageKey);
    if (parsed) {
      const mc = categoryKeyToMessageContent(parsed.categoryKey);
      if (mc !== MessageContent.NONE) msgContents.add(mc);
    }
  });

  // Build all byte ranges across all units
  const ranges: ByteRange[] = [];
  const categoryOf = (row: Ln1KeyMappingRow): MessageContent => {
    const parsed = parseCompositeKey(row.ln2MessageKey);
    return parsed ? categoryKeyToMessageContent(parsed.categoryKey) : MessageContent.NONE;
  };
  msgContents.forEach((mc) => {
    const brokenOut = mappings.some((m) => categoryOf(m) === mc && ownColumn(m));
    const size = brokenOut ? 0 : computeCategoryByteSize(mappings, mc, floors);
    for (let u = 0; u < units; u += 1) {
      const arr = unitOffsets[mc] || [];
      const start = arr[u] ?? 0;
      // Unchanged when no message of the category has a column of its own.
      const occupancy = brokenOut
        ? rowsOccupancy(mappings, (m) => categoryOf(m) === mc && !ownColumn(m), floors, start)
        : categoryOccupancy(mappings, mc, floors, start);
      const end = brokenOut ? occupancy.end : start + size;
      if (end > start) {
        ranges.push({ start, end, msgContent: mc, unitIndex: u, column: String(mc), occupancy });
      }
    }
  });
  Object.entries(individualOffsets).forEach(([key, pair]) => {
    if (!pair) return;
    const mc = mappings.find((m) => m.ln2MessageKey === key && m.units_based);
    if (!mc) return;
    const msgContent = categoryOf(mc);
    if (msgContent === MessageContent.NONE) return;
    const [initial, second] = pair;
    const delta = (second ?? initial) - initial;
    for (let u = 0; u < units; u += 1) {
      const start = initial + u * delta;
      const occupancy = rowsOccupancy(mappings, (m) => m.ln2MessageKey === key, floors, start);
      if (occupancy.end > start) {
        ranges.push({
          start,
          end: occupancy.end,
          msgContent,
          unitIndex: u,
          column: key,
          occupancy,
        });
      }
    }
  });

  const hasIntra = (mc: MessageContent, unitIdx: number): boolean => {
    const arr = intraContentFlags[mc];
    return arr ? arr[unitIdx] === true : false;
  };

  // Pairwise overlap check across all ranges (intra + inter unit)
  const cells = new Set<string>();
  const rowIndices = new Set<number>();
  const conflicts: Ln1CategoryConflict[] = [];
  for (let i = 0; i < ranges.length; i += 1) {
    for (let j = i + 1; j < ranges.length; j += 1) {
      const a = ranges[i];
      const b = ranges[j];
      if (a.start < b.end && b.start < a.end) {
        // Two columns of ONE category on one car share a register by design;
        // two messages of a category on one bit are `findLn1DuplicateIndices`'
        // to report, at bit granularity, as for the grouped column.
        if (a.unitIndex === b.unitIndex && a.msgContent === b.msgContent) {
          // Not a cross-category overlap — skip
        } else if (
          // Same unit, different categories, both flagged intra-content → allowed
          a.unitIndex === b.unitIndex &&
          a.msgContent !== b.msgContent &&
          hasIntra(a.msgContent, a.unitIndex) &&
          hasIntra(b.msgContent, b.unitIndex)
        ) {
          // Intentionally shared register — skip
        } else if (rangesActuallyCollide(a.occupancy, b.occupancy)) {
          // `column` is `String(mc)` for a grouped column, so its cell keeps
          // the "MessageContent:unitIndex" form the matrix paints.
          cells.add(`${a.column}:${a.unitIndex}`);
          cells.add(`${b.column}:${b.unitIndex}`);
          // Name the messages, not just the categories. Two ranges on the SAME
          // category are one category bleeding across units, and the rows are
          // identical on both sides — there is no pair of messages to blame, so
          // only the matrix cells are painted.
          if (a.msgContent !== b.msgContent) {
            occupancyCollisions(a.occupancy, b.occupancy).forEach((hit) => {
              rowIndices.add(hit.a.rowIndex);
              rowIndices.add(hit.b.rowIndex);
              conflicts.push({
                unitIndex: a.unitIndex,
                absByte: hit.absByte,
                aRowIndex: hit.a.rowIndex,
                bRowIndex: hit.b.rowIndex,
                aMsgContent: a.msgContent,
                bMsgContent: b.msgContent,
              });
            });
          }
        }
      }
    }
  }
  // Within-category duplicates are NOT detected here — findLn1DuplicateIndices
  // owns those, at bit granularity, via mappingDedupKey. The two are kept apart
  // on purpose: a shared byte BETWEEN categories is a conflict whatever bits are
  // wanted, because the register is read and written whole, while a shared byte
  // WITHIN one category is the normal case and only the same bit is a clash.
  // bank_level rows are excluded from duplicate detection since their offsets
  // are assigned at the bank matrix.

  return { cells, rowIndices, conflicts };
}

/**
 * Compute all overlapping (MessageContent, unitIndex) pairs across ALL units.
 *
 * The offset-matrix half of `detectLn1Overlaps`, kept as its own name because a
 * cell only needs to know whether it is red.
 */
export function computeLn1Overlaps(
  mappings: Ln1KeyMappingRow[],
  unitOffsets: Partial<Record<MessageContent, number[]>>,
  units: number,
  floors: number,
  intraContentFlags: Partial<Record<MessageContent, boolean[]>> = {},
): Set<string> {
  return detectLn1Overlaps(mappings, unitOffsets, units, floors, intraContentFlags).cells;
}

/** Convenience check for save-button gating. */
export function hasLn1Overlaps(
  mappings: Ln1KeyMappingRow[],
  unitOffsets: Partial<Record<MessageContent, number[]>>,
  units: number,
  floors: number,
  intraContentFlags: Partial<Record<MessageContent, boolean[]>> = {},
): boolean {
  return computeLn1Overlaps(mappings, unitOffsets, units, floors, intraContentFlags).size > 0;
}

// ************************************************ */
// COMPONENT ************************************** */

export default function CdoLn1MappingEditor({
  mappings,
  onMappingsChange,
  unitOffsets,
  onUnitOffsetsChange,
  intraContentFlags,
  onIntraContentFlagsChange,
  units,
  floors,
  onUnitsChange,
  hideOffsetGrid = false,
  hideMappingGrid = false,
  ln2BankLift = undefined,
  onLn2BankLiftChange = undefined,
  bankMatrixPerLift = undefined,
  individualOffsets = undefined,
  directionAware = false,
}: CdoLn1MappingEditorProps) {
  // Track cells at row >= 2 the user has explicitly edited
  const overriddenCells = useRef(new Set<string>());
  const rootRef = useRef<HTMLDivElement | null>(null);

  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // VIEW MODE & PAGINATION ************************** */

  const [viewMode, setViewMode] = useState<ViewMode>("normal");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Initialize from prop: if any intra-content flag is true, mode is ON
  const [intraContentOverlapMode, setIntraContentOverlapMode] = useState(() =>
    Object.values(intraContentFlags).some((arr) => arr?.some(Boolean)),
  );

  // Sync local intra-content state when the prop changes (e.g., switching
  // between adapters without unmounting). Without this, the useState
  // initializer only runs on first mount and the toggle leaks across adapters.
  useEffect(() => {
    const propValue = Object.values(intraContentFlags).some((arr) => arr?.some(Boolean));
    setIntraContentOverlapMode(propValue);
  }, [intraContentFlags]);

  /**
   * Categories this adapter actually maps — the only ones worth offering as
   * intra-content candidates.
   */
  const intraCategoryOptions = useMemo(() => {
    const present = new Set<MessageContent>();
    mappings.forEach((m) => {
      if (!m.ln2MessageKey) return;
      const parsed = parseCompositeKey(m.ln2MessageKey);
      if (!parsed) return;
      const mc = categoryKeyToMessageContent(parsed.categoryKey);
      if (mc !== MessageContent.NONE) present.add(mc);
    });
    return LN2_MESSAGE_CATEGORIES.filter((c) => present.has(c.messageContent)).map((c) => ({
      value: c.messageContent as number,
      label: c.label,
    }));
  }, [mappings]);

  /** MessageContent values currently declared as sharing a register block. */
  const intraSelected = useMemo(
    () =>
      Object.entries(intraContentFlags)
        .filter(([, arr]) => (arr as boolean[] | undefined)?.some(Boolean))
        .map(([mc]) => Number(mc))
        .filter((mc) => Number.isFinite(mc) && mc > 0),
    [intraContentFlags],
  );

  /** The same declaration as `intraSelected`, keyed the way the dedup logic reads it. */
  const intraSharedCategoryKeys = useMemo(
    () => intraContentCategories(intraContentFlags as Record<string | number, unknown>),
    [intraContentFlags],
  );

  /**
   * Write the flag map for every mapped category: true for the ones selected,
   * false for the rest. Writing the false entries too keeps a de-selection
   * from leaving a stale true behind.
   */
  const handleIntraCategoriesChange = (selected: number[]) => {
    const chosen = new Set(selected);
    const updated: Partial<Record<MessageContent, boolean[]>> = {};
    intraCategoryOptions.forEach((opt) => {
      updated[opt.value as MessageContent] = Array.from({ length: units }, () =>
        chosen.has(opt.value),
      );
    });
    setIntraContentOverlapMode(selected.length > 0);
    onIntraContentFlagsChange(updated);
  };

  // ************************************************ */
  // CATEGORY FILTER ********************************* */

  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [searchLn2, setSearchLn2] = useState<string>("");
  const [searchVendor, setSearchVendor] = useState<string>("");

  /** The single-filter category key (for auto-coloring new rows). */
  const singleFilterCatKey = categoryFilter.length === 1 ? categoryFilter[0] : "";

  /** Indices of mappings that pass the category filter and search terms, sorted by category. */
  const visibleIndices = useMemo(() => {
    const ln2Needle = normalizeLn2Needle(searchLn2);
    const vendorNeedle = searchVendor.trim();
    const allowed = categoryFilter.length > 0 ? new Set(categoryFilter) : null;
    const indices: number[] = [];
    mappings.forEach((m, i) => {
      if (allowed) {
        if (m.ln2MessageKey) {
          const parsed = parseCompositeKey(m.ln2MessageKey);
          if (!parsed || !allowed.has(parsed.categoryKey)) return;
        }
      }
      if (ln2Needle) {
        const haystack = normalizeLn2Needle(
          `${m.ln2MessageKey} ${m.ln2MessageKey ? getDisplayName(m.ln2MessageKey) : ""}`,
        );
        if (!haystack.includes(ln2Needle)) return;
      }
      if (
        vendorNeedle &&
        !`${m.byte_offset} ${m.bit_offset} ${m.offset} ${m.value}`.includes(vendorNeedle)
      ) {
        return;
      }
      indices.push(i);
    });
    // Sort by category only — sorting by byte/bit offset causes rows to jump
    // while the user is actively editing numeric fields.
    indices.sort((a, b) => {
      const catA = getCategoryKey(mappings[a].ln2MessageKey);
      const catB = getCategoryKey(mappings[b].ln2MessageKey);
      if (!catA && !catB) return a - b;
      if (!catA) return 1;
      if (!catB) return -1;
      const catCmp = catA.localeCompare(catB);
      if (catCmp !== 0) return catCmp;
      return a - b;
    });
    return indices;
  }, [mappings, categoryFilter, searchLn2, searchVendor]);

  /** Per-category numbering: maps global index → 1-based number within its category. */
  const categoryNumbering = useMemo(() => {
    const counters = new Map<string, number>();
    const result = new Map<number, number>();
    visibleIndices.forEach((idx) => {
      const cat = getCategoryKey(mappings[idx].ln2MessageKey) || "_unassigned";
      const n = (counters.get(cat) || 0) + 1;
      counters.set(cat, n);
      result.set(idx, n);
    });
    return result;
  }, [visibleIndices, mappings]);

  /** Paginated slice of visible indices. */
  const paginatedIndices = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return visibleIndices.slice(start, start + pageSize);
  }, [visibleIndices, currentPage, pageSize]);

  /**
   * Merged adapter-matrix value map for overlap detection.
   * Combines grouped categories (keyed by category name, derived from unitOffsets)
   * and individual columns (keyed by composite key, derived from individualOffsets).
   * Used to supply detectMappingRowOverlaps with the context it needs to determine
   * whether a duplicate pair is differentiated by the adapter matrix.
   */
  const adapterMatrixValueForDetection = useMemo<
    Record<string, { initial: number; delta: number }>
  >(() => {
    const MC_TO_CAT: Record<number, string> = {
      1: "status",
      2: "traffic",
      3: "usage",
      4: "service",
      5: "control",
      6: "fault",
      7: "group_supervisory_status",
      8: "group_control",
      9: "controller_info",
    };
    const out: Record<string, { initial: number; delta: number }> = {};
    Object.entries(unitOffsets).forEach(([mcStr, arr]) => {
      if (!Array.isArray(arr) || arr.length === 0) return;
      const mc = Number(mcStr);
      const catKey = MC_TO_CAT[mc];
      if (!catKey) return;
      const initial = arr[0] ?? 0;
      const delta = arr.length > 1 ? (arr[1] ?? initial) - initial : 0;
      out[catKey] = { initial, delta };
    });
    const indiv = individualOffsets ?? {};
    Object.entries(indiv).forEach(([compositeKey, pair]) => {
      if (!pair) return;
      const initial = pair[0] ?? 0;
      const delta = (pair[1] ?? initial) - initial;
      out[compositeKey] = { initial, delta };
    });
    return out;
  }, [unitOffsets, individualOffsets]);

  const bankMatrixPerLiftForDetection = useMemo<ReadonlyArray<Record<string, number>>>(
    () => bankMatrixPerLift ?? [],
    [bankMatrixPerLift],
  );

  /**
   * Build the duplicate-detection context when the adapter matrix is populated.
   * Falls back to undefined (legacy path) when no offsets exist.
   */
  const duplicateDetectionContext = useMemo<FindLn1DuplicateContext | undefined>(() => {
    if (Object.keys(adapterMatrixValueForDetection).length === 0) return undefined;
    return {
      adapterMatrix: adapterMatrixValueForDetection,
      bankMatrixPerLift: bankMatrixPerLiftForDetection,
      numCars: units,
      directionAware,
    };
  }, [adapterMatrixValueForDetection, bankMatrixPerLiftForDetection, units, directionAware]);

  /** Set of mapping indices that have duplicate (byte_offset,bit_offset,data) or (offset,value). */
  const duplicateIndices = useMemo(
    () => findLn1DuplicateIndices(mappings, intraSharedCategoryKeys, duplicateDetectionContext),
    [mappings, intraSharedCategoryKeys, duplicateDetectionContext],
  );

  /** How many rows in view the bulk direction control would actually change. */
  const visibleDirectionTargets = useMemo(
    () => visibleIndices.filter((i) => !mappings[i].is_pair_secondary).length,
    [visibleIndices, mappings],
  );

  const handleFilterChange = (val: string[]) => {
    setCategoryFilter(val);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (_current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // ************************************************ */
  // MAPPING HANDLERS ******************************** */
  const addMapping = () => {
    const lastRow = mappings.length > 0 ? mappings[mappings.length - 1] : null;
    const newRow = lastRow ? nextMapping(lastRow) : defaultMapping();
    onMappingsChange([...mappings, newRow]);
    const newTotal = visibleIndices.length + 1;
    setCurrentPage(Math.ceil(newTotal / pageSize));
  };

  const removeMapping = (idx: number) => {
    const deletedRow = mappings[idx];
    let base = mappings;

    // Clean up the paired partner before removing
    if (deletedRow.pair_id) {
      base = mappings.map((row, i) => {
        if (i !== idx && row.pair_id === deletedRow.pair_id) {
          return { ...row, pair_id: null, is_pair_secondary: false };
        }
        return row;
      });
    }

    const updated = base.filter((_, i) => i !== idx);
    onMappingsChange(updated);
    const newTotal = visibleIndices.length - 1;
    const maxPage = Math.max(1, Math.ceil(newTotal / pageSize));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  };

  const updateMapping = (idx: number, patch: Partial<Ln1KeyMappingRow>) => {
    const updated = [...mappings];
    updated[idx] = { ...updated[idx], ...patch };
    onMappingsChange(updated);
  };

  /**
   * Set one direction on every row currently in view.
   *
   * Direction is a per-key field, but it is almost never decided per key: an
   * operator decides that Status is read from the controller and Control is
   * written to it, and then applies that to all eighteen Status rows one
   * dropdown at a time. Doing it by hand is slow and, worse, easy to leave
   * half-done -- and a single row left at BOTH is not a small error. It makes
   * the whole category claim both LN1 maps, which is exactly what the overlap
   * detector reads to decide whether a shared byte is a real conflict, so one
   * missed row silently brings back every conflict the split was meant to
   * resolve.
   *
   * Scoped to the rows in view rather than to a category argument, so the
   * category filter and the search box both compose with it and what the button
   * says it will change is exactly what is on screen.
   *
   * Paired secondaries are skipped: a pair is ONE stored key and the primary's
   * direction covers it, which is why its own control is disabled.
   */
  const setDirectionForVisible = (direction: KeyDirection) => {
    const targets = new Set(visibleIndices.filter((i) => !mappings[i].is_pair_secondary));
    if (targets.size === 0) return;

    onMappingsChange(mappings.map((row, i) => (targets.has(i) ? { ...row, direction } : row)));
  };

  /**
   * The per-row Direction control, rendered identically in all three view
   * modes.
   *
   * Defined once rather than inlined three times: `group_shared` and its
   * siblings are duplicated across normal/compact/expanded, and a control added
   * to only one of them is a control most users never find. Callers pass the
   * wrapper width so each layout keeps its own spacing.
   *
   * Returns null when the adapter has not opted in, so an adapter without
   * direction_aware_keys shows exactly the columns it always did.
   *
   * A paired secondary row renders disabled at its primary's value: a pair is
   * ONE stored key (the secondary populates the lower_* group), so it cannot
   * carry a direction of its own — showing an editable control there would
   * offer a choice the wire cannot represent.
   */
  const renderDirectionControl = (
    row: Ln1KeyMappingRow,
    idx: number,
    width: number,
  ): React.ReactElement | null => {
    if (!directionAware) return null;

    const primary = row.is_pair_secondary
      ? (mappings.find((m) => m.pair_id === row.pair_id && !m.is_pair_secondary) ?? row)
      : row;

    // The server REFUSES a non-BOTH direction unless the adapter has
    // direction_aware_keys on (checkKeyDirection in LiftCloud's
    // key_direction.go). Offering the control anyway meant the operator picked
    // a direction, saved, got a rejection the UI discarded, and on reopening
    // found the old value back — indistinguishable from "saving is broken".
    // Say why instead, and point at the switch that fixes it.
    const directionLocked = !directionAware;
    const directionTitle = directionLocked
      ? "Direction is disabled because this adapter has Direction-Aware Keys turned off. The server rejects any direction other than Both while it is off. Turn it on in the adapter's settings above, then set directions here."
      : "Which LN1 map this key feeds. Both — the key is translated in each direction, as it always was. To device — the key is only written to the controller. From device — the key is only read from it. Two keys may share one address when one is To device and the other From device.";

    return (
      <Tooltip title={directionTitle}>
        <div style={{ width, flexShrink: 0 }}>
          <Select
            size="small"
            style={{ width: "100%" }}
            disabled={row.is_pair_secondary || directionLocked}
            value={primary.direction ?? KeyDirection.BOTH}
            onChange={(v: KeyDirection) => updateMapping(idx, { direction: v })}
            options={[
              { value: KeyDirection.BOTH, label: "Both" },
              { value: KeyDirection.TO_DEVICE, label: "To device" },
              { value: KeyDirection.FROM_DEVICE, label: "From device" },
            ]}
            data-testid={`ln1-direction-${idx}`}
            data-doc-label="Direction"
          />
        </div>
      </Tooltip>
    );
  };

  /** Returns indices of rows eligible to be paired with the row at `sourceIdx`. */
  const getEligiblePartners = (sourceIdx: number): number[] => {
    const source = mappings[sourceIdx];
    if (!source || source.contentType !== "bit") return [];
    const sourceCatKey = getCategoryKey(source.ln2MessageKey);
    return mappings.reduce<number[]>((acc, row, i) => {
      if (i === sourceIdx) return acc;
      if (row.contentType !== "bit") return acc;
      if (row.pair_id !== null) return acc; // already paired
      if (row.byte_offset !== source.byte_offset) return acc;
      if (row.bit_offset !== source.bit_offset) return acc;
      if (row.data === source.data) return acc; // must be opposite
      if (getCategoryKey(row.ln2MessageKey) !== sourceCatKey) return acc;
      if (row.floors_based !== source.floors_based) return acc;
      if (row.units_based !== source.units_based) return acc;
      acc.push(i);
      return acc;
    }, []);
  };

  const handlePair = (sourceIdx: number, partnerIdx: number) => {
    const newPairId = crypto.randomUUID();
    const updated = mappings.map((row, i) => {
      if (i === sourceIdx) return { ...row, pair_id: newPairId, is_pair_secondary: false };
      if (i === partnerIdx) return { ...row, pair_id: newPairId, is_pair_secondary: true };
      return row;
    });
    onMappingsChange(updated);
  };

  const handleUnpair = (idx: number) => {
    const row = mappings[idx];
    if (!row.pair_id) return;
    const pairId = row.pair_id;
    const updated = mappings.map((r) => {
      if (r.pair_id === pairId) {
        return { ...r, pair_id: null, is_pair_secondary: false };
      }
      return r;
    });
    onMappingsChange(updated);
  };

  const onChangeMessage = (idx: number, key: string) => {
    const patch: Partial<Ln1KeyMappingRow> = { ln2MessageKey: key };
    if (key) {
      const parsed = parseCompositeKey(key);
      if (parsed && mappings[idx].contentType === "byte" && parsed.variant !== "valued") {
        patch.ln2MessageKey = `${parsed.categoryKey}:valued:${parsed.enumValue}`;
      }
    }
    // Default the group-shared toggle from the (new) category: on for the
    // group-scoped categories (Group Supervisory Status / Group Control),
    // off otherwise.
    patch.group_shared = isGroupScopedCategory(getCategoryKey(patch.ln2MessageKey ?? key));
    updateMapping(idx, patch);
  };

  const onChangeContentType = (idx: number, ct: "bit" | "byte") => {
    const row = mappings[idx];
    const patch: Partial<Ln1KeyMappingRow> = { contentType: ct };
    if (ct === "byte" && row.ln2MessageKey) {
      const parsed = parseCompositeKey(row.ln2MessageKey);
      if (parsed && parsed.variant !== "valued") {
        patch.ln2MessageKey = `${parsed.categoryKey}:valued:${parsed.enumValue}`;
      }
    }
    updateMapping(idx, patch);
  };

  // ************************************************ */
  // UNIT OFFSETS ************************************ */

  /** Categories from mappings that have units_based=true, keyed by MessageContent */
  const unitBasedCategories = useMemo(() => {
    const cats = new Map<MessageContent, string>();
    mappings.forEach((m) => {
      if (!m.units_based || !m.ln2MessageKey) return;
      const parsed = parseCompositeKey(m.ln2MessageKey);
      if (!parsed) return;
      const mc = categoryKeyToMessageContent(parsed.categoryKey);
      if (mc === MessageContent.NONE) return;
      const cat = LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === parsed.categoryKey);
      if (cat && !cats.has(mc)) {
        cats.set(mc, cat.label);
      }
    });
    return Array.from(cats.entries());
  }, [mappings]);

  const updateUnitOffset = (mc: MessageContent, unitIdx: number, value: number) => {
    const newOffsets = { ...unitOffsets };
    const arr = [...(newOffsets[mc] || [])];
    while (arr.length < units) arr.push(0);
    arr[unitIdx] = value;

    if ((unitIdx === 0 || unitIdx === 1) && units >= 3) {
      const mcKeys = unitBasedCategories.map(([k]) => k);
      for (let c = 0; c < mcKeys.length; c += 1) {
        const mcKey = mcKeys[c];
        const catArr = mcKey === mc ? arr : [...(newOffsets[mcKey] || [])];
        while (catArr.length < units) catArr.push(0);
        if (catArr.length >= 2) {
          const delta = catArr[1] - catArr[0];
          for (let i = 2; i < units; i += 1) {
            if (!overriddenCells.current.has(`${mcKey}:${i}`)) {
              catArr[i] = catArr[0] + i * delta;
            }
          }
        }
        newOffsets[mcKey] = catArr;
      }
    } else {
      newOffsets[mc] = arr;
    }

    if (unitIdx >= 2) {
      overriddenCells.current.add(`${mc}:${unitIdx}`);
    }

    onUnitOffsetsChange(newOffsets);
  };

  const toggleIntraContent = (mc: MessageContent, unitIdx: number) => {
    const newFlags = { ...intraContentFlags };
    const arr = [...(newFlags[mc] || [])];
    while (arr.length <= unitIdx) arr.push(false);
    const newValue = !arr[unitIdx];
    arr[unitIdx] = newValue;
    newFlags[mc] = arr;

    // Apply the same flag to all other categories at this unit with the same offset
    const thisOffset = (unitOffsets[mc] || [])[unitIdx] ?? 0;
    unitBasedCategories.forEach(([otherMc]) => {
      if (otherMc === mc) return;
      const otherOffset = (unitOffsets[otherMc] || [])[unitIdx] ?? 0;
      if (otherOffset === thisOffset) {
        const otherArr = [...(newFlags[otherMc] || [])];
        while (otherArr.length <= unitIdx) otherArr.push(false);
        otherArr[unitIdx] = newValue;
        newFlags[otherMc] = otherArr;
      }
    });

    onIntraContentFlagsChange(newFlags);
  };

  /** Add a unit row and auto-fill it. */
  const handleAddUnit = () => {
    const newUnits = units + 1;
    onUnitsChange(newUnits);

    if (newUnits >= 3 && unitBasedCategories.length > 0) {
      const newOffsets = { ...unitOffsets };
      for (let c = 0; c < unitBasedCategories.length; c += 1) {
        const mc = unitBasedCategories[c][0];
        const arr = [...(newOffsets[mc] || [])];
        while (arr.length < newUnits) arr.push(0);
        if (arr.length >= 2) {
          const delta = arr[1] - arr[0];
          arr[newUnits - 1] = arr[0] + (newUnits - 1) * delta;
        }
        newOffsets[mc] = arr;
      }
      onUnitOffsetsChange(newOffsets);
    }
  };

  // ************************************************ */
  // OVERLAP DETECTION ******************************* */

  const overlapResult = useMemo(
    () =>
      detectLn1Overlaps(
        mappings,
        unitOffsets,
        units,
        floors,
        intraContentFlags,
        individualOffsets ?? {},
      ),
    [mappings, unitOffsets, units, floors, intraContentFlags, individualOffsets],
  );
  const overlappingCells = overlapResult.cells;

  /**
   * Rows that take part in a cross-category byte collision, and what each one
   * collides with.
   *
   * Separate from `duplicateIndices` on purpose: that one is a clash INSIDE a
   * message type, which the operator fixes by moving this row's byte or bit,
   * and this one is a clash BETWEEN message types, which the operator fixes by
   * moving a category's offset on the matrix above. Painting both the same red
   * told them a row was wrong and left them to work out which of the two
   * remedies applied.
   */
  const categoryConflictReasons = useMemo(() => {
    const byRow = new Map<number, string[]>();
    const nameOf = (rowIdx: number): string => {
      const key = mappings[rowIdx]?.ln2MessageKey ?? "";
      const label = getDisplayName(key);
      return label === "--" ? key : label;
    };
    const add = (rowIdx: number, text: string): void => {
      const arr = byRow.get(rowIdx);
      if (arr) {
        if (!arr.includes(text)) arr.push(text);
        return;
      }
      byRow.set(rowIdx, [text]);
    };
    overlapResult.conflicts.forEach((c) => {
      add(
        c.aRowIndex,
        `shares byte ${c.absByte} on unit ${c.unitIndex + 1} with ${nameOf(c.bRowIndex)}`,
      );
      add(
        c.bRowIndex,
        `shares byte ${c.absByte} on unit ${c.unitIndex + 1} with ${nameOf(c.aRowIndex)}`,
      );
    });
    return byRow;
  }, [overlapResult, mappings]);

  /**
   * The two conflict states one row can be in, and the text that explains them.
   *
   * Returned together so the three views cannot drift about which class means
   * what — the compact view carries the same two states on different CSS
   * properties, and that is the only thing that differs between them.
   */
  const conflictStateFor = (
    idx: number,
  ): { isDupe: boolean; isCategoryConflict: boolean; className: string; title: string } => {
    const isDupe = duplicateIndices.has(idx);
    const reasons = categoryConflictReasons.get(idx);
    const isCategoryConflict = reasons !== undefined;
    const classes: string[] = [];
    const parts: string[] = [];
    if (isDupe) {
      classes.push("ln1Row__conflictInternal");
      parts.push(
        "Conflict inside this message type: another mapping of the same type claims " +
          "the same byte and bit. Move this row's byte or bit offset.",
      );
    }
    if (isCategoryConflict) {
      classes.push("ln1Row__conflictCategory");
      parts.push(
        `Conflict between message types: this mapping ${reasons.join("; ")}. ` +
          "Move a category offset in the Offset Matrix rather than this row.",
      );
    }
    return {
      isDupe,
      isCategoryConflict,
      className: classes.join(" "),
      title: parts.join(" "),
    };
  };

  // ************************************************ */
  // TABLE CONFIG ************************************ */

  const offsetTableData = useMemo(() => {
    const rows: { key: number; unitIndex: number }[] = [];
    for (let i = 0; i < units; i += 1) {
      rows.push({ key: i, unitIndex: i });
    }
    return rows;
  }, [units]);

  const offsetColumns = useMemo(() => {
    const cols: {
      title: string;
      key: string;
      dataIndex?: string;
      render?: (_: unknown, record: { unitIndex: number }) => React.ReactNode;
      width?: number;
    }[] = [
      {
        title: "Unit",
        key: "unit",
        width: 60,
        render: (_, record) => <Tag>{record.unitIndex + 1}</Tag>,
      },
    ];
    unitBasedCategories.forEach(([mc, catLabel]) => {
      const size = computeCategoryByteSize(mappings, mc, floors);
      const hasPaired = mappings.some((m) => {
        const parsed = parseCompositeKey(m.ln2MessageKey);
        return (
          parsed && categoryKeyToMessageContent(parsed.categoryKey) === mc && m.pair_id !== null
        );
      });
      cols.push({
        title: hasPaired
          ? `${catLabel} (${size}B) — Primary / Secondary`
          : `${catLabel} (${size}B)`,
        key: String(mc),
        render: (_, record) => {
          const arr = unitOffsets[mc] || [];
          const cellValue = arr[record.unitIndex] ?? 0;
          const isOverlap = overlappingCells.has(`${mc}:${record.unitIndex}`);
          const flagArr = intraContentFlags[mc] || [];
          const isIntra = flagArr[record.unitIndex] === true;
          // Build a helpful tooltip listing the conflicting categories and the
          // three ways the user can resolve a real overlap at the bank matrix
          // level: move one offset, change its size, or accept via intra-content.
          const conflictingLabels = isOverlap
            ? unitBasedCategories
                .filter(([otherMc]) => {
                  if (otherMc === mc) return false;
                  const otherSize = computeCategoryByteSize(mappings, otherMc, floors);
                  const otherStart = (unitOffsets[otherMc] || [])[record.unitIndex] ?? 0;
                  return cellValue < otherStart + otherSize && otherStart < cellValue + size;
                })
                .map(([, l]) => l)
            : [];
          const overlapTooltip =
            conflictingLabels.length > 0
              ? `${catLabel} byte range [${cellValue}, ${
                  cellValue + size
                }) overlaps ${conflictingLabels.join(
                  ", ",
                )}. Fix options: change this offset, change the other category's offset, or enable intra-content for this unit to accept a shared register.`
              : `Byte range [${cellValue}, ${cellValue + size}) overlaps another category`;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Tooltip
                title={isOverlap ? overlapTooltip : `Bytes [${cellValue}, ${cellValue + size})`}
              >
                <InputNumber
                  size="small"
                  min={0}
                  value={cellValue}
                  onChange={(v) => updateUnitOffset(mc, record.unitIndex, v ?? 0)}
                  style={{
                    width: 80,
                    ...(isOverlap ? OVERLAP_STYLE : {}),
                  }}
                  status={isOverlap ? "error" : undefined}
                />
              </Tooltip>
              <Tooltip title="Allow shared register (intra-content)">
                <Checkbox
                  checked={isIntra}
                  onChange={() => toggleIntraContent(mc, record.unitIndex)}
                />
              </Tooltip>
            </div>
          );
        },
      });
    });
    return cols;
  }, [
    unitBasedCategories,
    unitOffsets,
    units,
    overlappingCells,
    mappings,
    floors,
    intraContentFlags,
  ]);

  const showAddUnit = units >= 2 && unitBasedCategories.length > 0;

  // ************************************************ */
  // RESOLVE CATEGORY COLOR ************************** */

  const effectiveCatKey = (row: Ln1KeyMappingRow): string => {
    const actual = getCategoryKey(row.ln2MessageKey);
    if (actual) return actual;
    return singleFilterCatKey;
  };

  const effectiveCatLabel = (row: Ln1KeyMappingRow): string | null => {
    const actual = getCategoryLabel(row.ln2MessageKey);
    if (actual) return actual;
    if (singleFilterCatKey) return labelForCategoryKey(singleFilterCatKey);
    return null;
  };

  // ************************************************ */
  // RENDER HELPERS ********************************** */

  const renderTag = (catKey: string, label: string | null, idx: number) => (
    <Tag
      style={
        catKey
          ? {
              backgroundColor: categoryColor(catKey),
              color: "#fff",
              border: "none",
            }
          : undefined
      }
      color={catKey ? undefined : "default"}
    >
      {label
        ? `${label} #${categoryNumbering.get(idx) ?? idx + 1}`
        : `Key #${categoryNumbering.get(idx) ?? idx + 1}`}
    </Tag>
  );

  const renderDeleteBtn = (idx: number) => (
    <Tooltip title="Remove mapping">
      <Button type="text" danger size="small" onClick={() => removeMapping(idx)}>
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    </Tooltip>
  );

  const catFilterProp = categoryFilter.length > 0 ? categoryFilter : undefined;

  // ************************************************ */
  // VIEW: NORMAL (single horizontal row per card) *** */

  const renderNormal = (row: Ln1KeyMappingRow, idx: number) => {
    const isBit = row.contentType === "bit";
    const catKey = effectiveCatKey(row);
    const catLabel = effectiveCatLabel(row);
    const conflict = conflictStateFor(idx);
    const borderColor = resolveBorderColor(conflict.isDupe, catKey, true);
    const eligiblePartners = isBit && !row.pair_id ? getEligiblePartners(idx) : [];

    return (
      <div
        key={`ln1-key-${String(idx)}`}
        className={conflict.className}
        title={conflict.title || undefined}
        style={{
          border: `2px solid ${borderColor}`,
          borderRadius: 6,
          padding: "8px 12px",
          marginBottom: 6,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {renderTag(catKey, catLabel, idx)}
          <div style={{ flex: "1 1 180px", minWidth: 140 }}>
            <SelectLn2Message
              value={row.ln2MessageKey}
              onChange={(key: string) => onChangeMessage(idx, key)}
              disabled={false}
              categoryFilter={catFilterProp}
            />
          </div>
          <div>
            <span className="generalStyles__inputLabel" style={{ marginRight: 4, fontSize: 11 }}>
              Type
            </span>
            <Select
              size="small"
              value={row.contentType}
              onChange={(v: "bit" | "byte") => onChangeContentType(idx, v)}
              options={CONTENT_TYPE_OPTIONS_SHORT}
              style={{ width: 70 }}
            />
          </div>
          {isBit ? (
            <>
              <div>
                <span
                  className="generalStyles__inputLabel"
                  style={{ marginRight: 4, fontSize: 11 }}
                >
                  Byte
                </span>
                <InputNumber
                  size="small"
                  min={0}
                  value={row.byte_offset}
                  onChange={(v) => updateMapping(idx, { byte_offset: v ?? 0 })}
                  style={{ width: 58 }}
                  disabled={row.is_pair_secondary}
                />
              </div>
              <div>
                <span
                  className="generalStyles__inputLabel"
                  style={{ marginRight: 4, fontSize: 11 }}
                >
                  Bit
                </span>
                <InputNumber
                  size="small"
                  min={0}
                  max={7}
                  value={row.bit_offset}
                  onChange={(v) => updateMapping(idx, { bit_offset: v ?? 0 })}
                  style={{ width: 50 }}
                  disabled={row.is_pair_secondary}
                />
              </div>
              <div>
                <span
                  className="generalStyles__inputLabel"
                  style={{ marginRight: 4, fontSize: 11 }}
                >
                  Data
                </span>
                {row.floors_based ? (
                  <span style={{ opacity: 0.5, fontSize: 11 }}>Auto</span>
                ) : (
                  <Switch
                    size="small"
                    checked={row.data}
                    onChange={(checked) => updateMapping(idx, { data: checked })}
                    disabled={row.is_pair_secondary}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <span
                  className="generalStyles__inputLabel"
                  style={{ marginRight: 4, fontSize: 11 }}
                >
                  Off
                </span>
                <InputNumber
                  size="small"
                  min={0}
                  value={row.offset}
                  onChange={(v) => updateMapping(idx, { offset: v ?? 0 })}
                  style={{ width: 58 }}
                />
              </div>
              <div>
                <span
                  className="generalStyles__inputLabel"
                  style={{ marginRight: 4, fontSize: 11 }}
                >
                  Val
                </span>
                {row.floors_based ? (
                  <span style={{ opacity: 0.5, fontSize: 11 }}>Auto</span>
                ) : (
                  <InputNumber
                    size="small"
                    min={0}
                    value={row.value}
                    onChange={(v) => updateMapping(idx, { value: v ?? 0 })}
                    style={{ width: 58 }}
                  />
                )}
              </div>
            </>
          )}
          <div>
            <span className="generalStyles__inputLabel" style={{ marginRight: 4, fontSize: 11 }}>
              Unit
            </span>
            <Switch
              size="small"
              checked={row.units_based}
              onChange={(checked) => updateMapping(idx, { units_based: checked })}
            />
          </div>
          <div>
            <span className="generalStyles__inputLabel" style={{ marginRight: 4, fontSize: 11 }}>
              Floor
            </span>
            <Switch
              size="small"
              checked={row.floors_based}
              onChange={(checked) =>
                updateMapping(
                  idx,
                  checked ? { floors_based: true, data: true } : { floors_based: false },
                )
              }
            />
          </div>
          <Tooltip title="Resolve this mapping individually at the bank matrix instead of sharing the category base offset">
            <div>
              <span className="generalStyles__inputLabel" style={{ marginRight: 4, fontSize: 11 }}>
                Bank
              </span>
              <Switch
                size="small"
                checked={row.bank_level}
                onChange={(checked) => updateMapping(idx, { bank_level: checked })}
              />
            </div>
          </Tooltip>
          <Tooltip title="Give this mapping its own column in the Offset Matrix, with its own per-car Initial Offset and Car Delta">
            <div>
              <span className="generalStyles__inputLabel" style={{ marginRight: 4, fontSize: 11 }}>
                Own
              </span>
              <Switch
                size="small"
                checked={row.individual}
                onChange={(checked) => updateMapping(idx, { individual: checked })}
              />
            </div>
          </Tooltip>
          <Tooltip title="Group-shared register: one register serves the whole group on every car (car delta 0). Keeps the shared address off the Offset Matrix's cross-car overlap check. Defaults on for Group Supervisory Status and Group Control.">
            <div>
              <span className="generalStyles__inputLabel" style={{ marginRight: 4, fontSize: 11 }}>
                Group
              </span>
              <Switch
                size="small"
                checked={row.group_shared}
                onChange={(checked) => updateMapping(idx, { group_shared: checked })}
              />
            </div>
          </Tooltip>
          {renderDirectionControl(row, idx, 110)}
          <Tooltip title="Debounce (ms)">
            <InputNumber
              size="small"
              min={0}
              placeholder="ms"
              value={row.debounce_time}
              onChange={(v) => updateMapping(idx, { debounce_time: v })}
              style={{ width: 60 }}
            />
          </Tooltip>
          {renderDeleteBtn(idx)}
          {isBit && (
            <>
              {row.pair_id ? (
                <Tooltip
                  title={row.is_pair_secondary ? "Secondary — unpair from primary" : "Unpair"}
                >
                  <Button
                    type="text"
                    size="small"
                    style={{ fontSize: 11, color: row.is_pair_secondary ? "#1890ff" : undefined }}
                    onClick={() => handleUnpair(idx)}
                  >
                    {row.is_pair_secondary ? "2nd" : "Unpair"}
                  </Button>
                </Tooltip>
              ) : (
                eligiblePartners.length > 0 && (
                  <Select
                    size="small"
                    placeholder="Pair…"
                    style={{ width: 80 }}
                    onChange={(partnerIdx: number) => handlePair(idx, partnerIdx)}
                    options={eligiblePartners.map((pi) => ({
                      value: pi,
                      label:
                        getCategoryLabel(mappings[pi].ln2MessageKey) ?? mappings[pi].ln2MessageKey,
                    }))}
                  />
                )
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ************************************************ */
  // VIEW: COMPACT *********************************** */

  const renderCompact = (row: Ln1KeyMappingRow, idx: number) => {
    const isBit = row.contentType === "bit";
    const catKey = effectiveCatKey(row);
    const conflict = conflictStateFor(idx);
    const borderColor = resolveBorderColor(conflict.isDupe, catKey, false);

    return (
      <div
        key={`ln1-key-${String(idx)}`}
        className={`ln1-compact-row ln1Row__compact ${conflict.className}`.trim()}
        title={conflict.title || undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "1px 2px",
          borderLeft: `2px solid ${borderColor}`,
          borderBottom: "1px solid #f0f0f0",
          minHeight: 26,
          flexWrap: "nowrap",
          fontSize: 12,
        }}
      >
        <div style={{ width: 300, flexShrink: 1 }}>
          <SelectLn2Message
            value={row.ln2MessageKey}
            onChange={(key: string) => onChangeMessage(idx, key)}
            disabled={false}
            categoryFilter={catFilterProp}
            compact
          />
        </div>
        <Select
          size="small"
          value={row.contentType}
          onChange={(v: "bit" | "byte") => onChangeContentType(idx, v)}
          options={CONTENT_TYPE_OPTIONS_SHORT}
          style={{ width: 56, flexShrink: 0 }}
        />
        {isBit ? (
          <>
            <Tooltip title="Byte Offset">
              <InputNumber
                size="small"
                min={0}
                value={row.byte_offset}
                onChange={(v) => updateMapping(idx, { byte_offset: v ?? 0 })}
                style={{ width: 50, flexShrink: 0 }}
              />
            </Tooltip>
            <Tooltip title="Bit Offset">
              <InputNumber
                size="small"
                min={0}
                max={7}
                value={row.bit_offset}
                onChange={(v) => updateMapping(idx, { bit_offset: v ?? 0 })}
                style={{ width: 44, flexShrink: 0 }}
              />
            </Tooltip>
            <Tooltip title={row.floors_based ? "Auto (floor expanded)" : "Data"}>
              <div style={{ width: 30, flexShrink: 0 }}>
                {row.floors_based ? (
                  <span style={{ opacity: 0.5, fontSize: 11 }}>Auto</span>
                ) : (
                  <Switch
                    size="small"
                    checked={row.data}
                    onChange={(checked) => updateMapping(idx, { data: checked })}
                  />
                )}
              </div>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Offset">
              <InputNumber
                size="small"
                min={0}
                value={row.offset}
                onChange={(v) => updateMapping(idx, { offset: v ?? 0 })}
                style={{ width: 50, flexShrink: 0 }}
              />
            </Tooltip>
            <Tooltip title={row.floors_based ? "Auto (floor expanded)" : "Value"}>
              {row.floors_based ? (
                <span style={{ width: 44, flexShrink: 0, opacity: 0.5, fontSize: 11 }}>Auto</span>
              ) : (
                <InputNumber
                  size="small"
                  min={0}
                  value={row.value}
                  onChange={(v) => updateMapping(idx, { value: v ?? 0 })}
                  style={{ width: 44, flexShrink: 0 }}
                />
              )}
            </Tooltip>
            <Tooltip title="Data">
              <div style={{ width: 30, flexShrink: 0 }} />
            </Tooltip>
          </>
        )}
        <Tooltip title="Per Unit">
          <div style={{ width: 30, flexShrink: 0 }}>
            <Switch
              size="small"
              checked={row.units_based}
              onChange={(checked) => updateMapping(idx, { units_based: checked })}
            />
          </div>
        </Tooltip>
        <Tooltip title="Per Floor">
          <div style={{ width: 30, flexShrink: 0 }}>
            <Switch
              size="small"
              checked={row.floors_based}
              onChange={(checked) =>
                updateMapping(
                  idx,
                  checked ? { floors_based: true, data: true } : { floors_based: false },
                )
              }
            />
          </div>
        </Tooltip>
        <Tooltip title="Bank Level — individual offset at bank matrix">
          <div style={{ width: 30, flexShrink: 0 }}>
            <Switch
              size="small"
              checked={row.bank_level}
              onChange={(checked) => updateMapping(idx, { bank_level: checked })}
            />
          </div>
        </Tooltip>
        <Tooltip title="Own Column — own column in the Offset Matrix with per-car Initial Offset and Car Delta">
          <div style={{ width: 30, flexShrink: 0 }}>
            <Switch
              size="small"
              checked={row.individual}
              onChange={(checked) => updateMapping(idx, { individual: checked })}
            />
          </div>
        </Tooltip>
        <Tooltip title="Group-shared register — one register shared by every car (car delta 0); off the cross-car overlap check. Defaults on for Group Supervisory Status / Group Control.">
          <div style={{ width: 30, flexShrink: 0 }}>
            <Switch
              size="small"
              checked={row.group_shared}
              onChange={(checked) => updateMapping(idx, { group_shared: checked })}
            />
          </div>
        </Tooltip>
        {renderDirectionControl(row, idx, 96)}
        <Tooltip title="Debounce (ms)">
          <InputNumber
            size="small"
            min={0}
            placeholder="ms"
            value={row.debounce_time}
            onChange={(v) => updateMapping(idx, { debounce_time: v })}
            style={{ width: 60, flexShrink: 0 }}
          />
        </Tooltip>
        <Tooltip title="Remove mapping">
          <Button
            type="text"
            danger
            size="small"
            onClick={() => removeMapping(idx)}
            style={{
              width: 28,
              flexShrink: 0,
              padding: "0 4px",
              height: 20,
              lineHeight: "20px",
            }}
          >
            <FontAwesomeIcon icon={faTrash} style={{ fontSize: 10 }} />
          </Button>
        </Tooltip>
      </div>
    );
  };

  // ************************************************ */
  // VIEW: EXPANDED (multi-row card) ***************** */

  const renderExpanded = (row: Ln1KeyMappingRow, idx: number) => {
    const isBit = row.contentType === "bit";
    const catKey = effectiveCatKey(row);
    const catLabel = effectiveCatLabel(row);
    const conflict = conflictStateFor(idx);
    const borderColor = resolveBorderColor(conflict.isDupe, catKey, true);

    return (
      <div
        key={`ln1-key-${String(idx)}`}
        className={conflict.className}
        title={conflict.title || undefined}
        style={{
          border: `2px solid ${borderColor}`,
          borderRadius: 6,
          padding: 12,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          {renderTag(catKey, catLabel, idx)}
          {renderDeleteBtn(idx)}
        </div>

        <div className="container">
          {/* Row 1: LN2 Message + Content Type */}
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputFlexRequired">*</span>
              <span className="generalStyles__inputLabel">LN2 Message</span>
              <div style={{ marginTop: 8 }}>
                <SelectLn2Message
                  value={row.ln2MessageKey}
                  onChange={(key: string) => onChangeMessage(idx, key)}
                  disabled={false}
                  categoryFilter={catFilterProp}
                />
              </div>
            </div>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputFlexRequired">*</span>
              <span className="generalStyles__inputLabel">Content Type</span>
              <div style={{ marginTop: 8 }}>
                <Select
                  className="generalStyles__width100"
                  size="large"
                  value={row.contentType}
                  onChange={(v: "bit" | "byte") => onChangeContentType(idx, v)}
                  options={CONTENT_TYPE_OPTIONS}
                />
              </div>
            </div>
          </div>

          {/* Row 2: Byte/Bit Offset or Offset/Value */}
          <div className="row">
            {isBit ? (
              <>
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Byte Offset</span>
                  <div style={{ marginTop: 8 }}>
                    <InputNumber
                      size="large"
                      min={0}
                      value={row.byte_offset}
                      onChange={(v) => updateMapping(idx, { byte_offset: v ?? 0 })}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Bit Offset</span>
                  <div style={{ marginTop: 8 }}>
                    <InputNumber
                      size="large"
                      min={0}
                      max={7}
                      value={row.bit_offset}
                      onChange={(v) => updateMapping(idx, { bit_offset: v ?? 0 })}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Offset</span>
                  <div style={{ marginTop: 8 }}>
                    <InputNumber
                      size="large"
                      min={0}
                      value={row.offset}
                      onChange={(v) => updateMapping(idx, { offset: v ?? 0 })}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Value</span>
                  <div style={{ marginTop: 8 }}>
                    {row.floors_based ? (
                      <div style={{ opacity: 0.5 }}>Auto</div>
                    ) : (
                      <InputNumber
                        size="large"
                        min={0}
                        value={row.value}
                        onChange={(v) => updateMapping(idx, { value: v ?? 0 })}
                        style={{ width: "100%" }}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Row 3: All Toggles */}
          <div className="row">
            {isBit && (
              <div className="col-12 col-md-3 mt-3">
                <span className="generalStyles__inputLabel">Data</span>
                <div style={{ marginTop: 8 }}>
                  {row.floors_based ? (
                    <span style={{ opacity: 0.5 }}>Auto</span>
                  ) : (
                    <Switch
                      checked={row.data}
                      onChange={(checked) => updateMapping(idx, { data: checked })}
                    />
                  )}
                </div>
              </div>
            )}
            <div className="col-12 col-md-3 mt-3">
              <span className="generalStyles__inputLabel">Per Unit</span>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={row.units_based}
                  onChange={(checked) => updateMapping(idx, { units_based: checked })}
                />
              </div>
            </div>
            <div className="col-12 col-md-3 mt-3">
              <span className="generalStyles__inputLabel">Per Floor</span>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={row.floors_based}
                  onChange={(checked) =>
                    updateMapping(
                      idx,
                      checked ? { floors_based: true, data: true } : { floors_based: false },
                    )
                  }
                />
              </div>
            </div>
            <div className="col-12 col-md-3 mt-3">
              <Tooltip title="Resolve this mapping individually at the bank matrix instead of sharing the category base offset">
                <span className="generalStyles__inputLabel">Bank Level</span>
              </Tooltip>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={row.bank_level}
                  onChange={(checked) => updateMapping(idx, { bank_level: checked })}
                />
              </div>
            </div>
            <div className="col-12 col-md-3 mt-3">
              <Tooltip title="Give this mapping its own column in the Offset Matrix, with its own per-car Initial Offset and Car Delta">
                <span className="generalStyles__inputLabel">Own Column</span>
              </Tooltip>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={row.individual}
                  onChange={(checked) => updateMapping(idx, { individual: checked })}
                />
              </div>
            </div>
            <div className="col-12 col-md-3 mt-3">
              <Tooltip title="Group-shared register: one register serves the whole group on every car (car delta 0); kept off the Offset Matrix's cross-car overlap check. Defaults on for Group Supervisory Status and Group Control.">
                <span className="generalStyles__inputLabel">Group Shared</span>
              </Tooltip>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={row.group_shared}
                  onChange={(checked) => updateMapping(idx, { group_shared: checked })}
                />
              </div>
            </div>
            {directionAware && (
              <div className="col-12 col-md-3 mt-3">
                <Tooltip title="Which LN1 map this key feeds. Both — translated in each direction, as it always was. To device — only written to the controller. From device — only read from it.">
                  <span className="generalStyles__inputLabel">Direction</span>
                </Tooltip>
                <div style={{ marginTop: 8 }}>{renderDirectionControl(row, idx, 160)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ************************************************ */
  // RENDER ****************************************** */

  const renderMapping = (row: Ln1KeyMappingRow, idx: number) => {
    if (viewMode === "compact") return renderCompact(row, idx);
    if (viewMode === "expanded") return renderExpanded(row, idx);
    return renderNormal(row, idx);
  };

  return (
    <div ref={rootRef}>
      {/* Bank / Lift selection via shared Ln2MappingEditor (message picker hidden) */}
      {ln2BankLift !== undefined && (
        <Ln2MappingEditor
          value={ln2BankLift}
          onChange={onLn2BankLiftChange ?? (() => {})}
          hideMessagePicker
        />
      )}

      {!hideMappingGrid && (
        <>
          <Divider orientation="left" plain>
            LN1 Key Mappings
          </Divider>

          {/* Toolbar: filter + search + view mode.
              Affix pins it to the top of the nearest scroll container
              (works inside Drawers, where position: sticky often fails due
              to ancestor overflow: hidden). */}
          <Affix
            offsetTop={0}
            target={() => findScrollTarget(rootRef.current)}
            style={{ zIndex: 10, marginBottom: 12 }}
          >
            <div
              className={`generalStyles__box__${theme}`}
              style={{
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 8,
                paddingRight: 8,
                borderRadius: 6,
                display: "flex",
                gap: 12,
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: "1 1 220px" }}>
                <span className="generalStyles__inputLabel">Filter by Category</span>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All categories"
                  className="generalStyles__width100"
                  size="small"
                  value={categoryFilter}
                  onChange={handleFilterChange}
                  options={CATEGORY_FILTER_OPTIONS}
                  style={{ marginTop: 4 }}
                />
              </div>
              {/* Set every row IN VIEW to one direction. Placed with the filter it
                  scopes to, because "all of Status" is expressed by filtering
                  to Status -- so the control needs no category argument and
                  what it changes is exactly what is on screen. Hidden while
                  direction-aware keys are off, matching the per-row control. */}
              {directionAware && (
                <div style={{ flex: "1 1 200px" }}>
                  <Tooltip
                    title={
                      visibleDirectionTargets === 0
                        ? "No editable rows in view. Paired secondaries take their direction from the primary."
                        : `Sets the direction on all ${visibleDirectionTargets} row(s) currently shown. Filter by category first to apply it to one category.`
                    }
                  >
                    <div>
                      <span className="generalStyles__inputLabel">Set Direction for Shown</span>
                      <Select
                        className="generalStyles__width100"
                        size="small"
                        placeholder={`Apply to ${visibleDirectionTargets} row(s)`}
                        value={null}
                        disabled={visibleDirectionTargets === 0}
                        onChange={(v: KeyDirection) => setDirectionForVisible(v)}
                        options={[
                          { value: KeyDirection.BOTH, label: "All -> Both" },
                          { value: KeyDirection.TO_DEVICE, label: "All -> To device" },
                          { value: KeyDirection.FROM_DEVICE, label: "All -> From device" },
                        ]}
                        style={{ marginTop: 4 }}
                        data-testid="ln1-bulk-direction"
                        data-doc-label="Set Direction for Shown"
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
              <div style={{ flex: "1 1 180px" }}>
                <span className="generalStyles__inputLabel">Search Ln2 Message</span>
                <Input
                  allowClear
                  size="small"
                  placeholder="e.g. Usage::Up"
                  value={searchLn2}
                  onChange={(e) => {
                    setSearchLn2(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ marginTop: 4 }}
                />
              </div>
              <div style={{ flex: "1 1 160px" }}>
                <span className="generalStyles__inputLabel">Search Vendor Offset</span>
                <Input
                  allowClear
                  size="small"
                  placeholder="e.g. 1000"
                  value={searchVendor}
                  onChange={(e) => {
                    setSearchVendor(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <span className="generalStyles__inputLabel" style={{ display: "block" }}>
                  View
                </span>
                <Radio.Group
                  size="small"
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  options={[
                    { label: "Compact", value: "compact" },
                    { label: "Normal", value: "normal" },
                    { label: "Expanded", value: "expanded" },
                  ]}
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <Tooltip title="Add new key mapping">
                  <Button
                    type="primary"
                    size="small"
                    onClick={addMapping}
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    style={{ marginTop: 4 }}
                  >
                    Add
                  </Button>
                </Tooltip>
              </div>
              <Tooltip title="Pick the message types that read from the SAME physical register block — on a Swift CEC, Status and Usage are two views of one 10-byte block. Their offsets are then allowed to coincide in the Offset Matrix, and their byte+bit positions must not collide with each other. Message types you leave unselected keep their own address space, so Fault byte 0 bit 0 and Control byte 0 bit 0 stay legal.">
                <div>
                  <span className="generalStyles__inputLabel" style={{ display: "block" }}>
                    Intra-content
                  </span>
                  <Select
                    mode="multiple"
                    allowClear
                    size="small"
                    style={{ minWidth: 220, marginTop: 6 }}
                    placeholder="None — each type has its own registers"
                    value={intraSelected}
                    options={intraCategoryOptions}
                    onChange={handleIntraCategoriesChange}
                    maxTagCount="responsive"
                  />
                </div>
              </Tooltip>
            </div>
          </Affix>

          {/* Compact header row */}
          {viewMode === "compact" && paginatedIndices.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "1px 2px 1px 4px",
                borderBottom: "2px solid #d9d9d9",
                fontSize: 10,
                color: "#888",
                fontWeight: 600,
              }}
            >
              <div style={{ width: 300, flexShrink: 1 }}>Message</div>
              <div style={{ width: 56, flexShrink: 0 }}>Type</div>
              <div style={{ width: 50, flexShrink: 0 }}>B.Off</div>
              <div style={{ width: 44, flexShrink: 0 }}>b/Val</div>
              <div style={{ width: 30, flexShrink: 0 }}>D</div>
              <div style={{ width: 30, flexShrink: 0 }}>U</div>
              <div style={{ width: 30, flexShrink: 0 }}>F</div>
              <div style={{ width: 30, flexShrink: 0 }}>BL</div>
              <div style={{ width: 28, flexShrink: 0 }} />
            </div>
          )}

          {/* Conflict summary. Two banners, not one, because the two conflicts
              are fixed in two different places and each banner names its own
              remedy — and because the banner is where the operator learns which
              animation on the rows below means which. */}
          {duplicateIndices.size > 0 && (
            <div
              style={{
                background: "rgba(255, 77, 79, 0.1)",
                border: "1px solid #ff4d4f",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 8,
              }}
            >
              <strong style={{ color: "#ff4d4f" }}>
                {duplicateIndices.size} duplicate mapping
                {duplicateIndices.size === 1 ? "" : "s"} detected
              </strong>
              <span style={{ marginLeft: 8, opacity: 0.8 }}>
                {intraContentOverlapMode
                  ? "— rows with identical byte AND bit across ANY message type pulse red below"
                  : "— only rows with identical byte AND bit WITHIN the same message type pulse red below (cross-category sharing allowed)"}
              </span>
            </div>
          )}

          {categoryConflictReasons.size > 0 && (
            <div
              style={{
                background: "rgba(250, 173, 20, 0.1)",
                border: "1px solid #faad14",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 8,
              }}
            >
              <strong style={{ color: "#d48806" }}>
                {categoryConflictReasons.size} mapping
                {categoryConflictReasons.size === 1 ? "" : "s"} in a message-type range conflict
              </strong>
              <span style={{ marginLeft: 8, opacity: 0.8 }}>
                — these rows outline amber below. Their byte is claimed by two different message
                types, so the fix is a category offset in the Offset Matrix above, not the row.
                Message types whose ranges merely overlap without any mapping sharing a byte are not
                counted.
              </span>
            </div>
          )}

          {/* Mapping cards */}
          {paginatedIndices.map((idx) => renderMapping(mappings[idx], idx))}

          {/* Pagination + Add button */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              marginBottom: 8,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Button type="dashed" onClick={addMapping}>
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
              Add Key Mapping
            </Button>
            {visibleIndices.length > 10 && (
              <Pagination
                size="small"
                current={currentPage}
                pageSize={pageSize}
                total={visibleIndices.length}
                onChange={setCurrentPage}
                onShowSizeChange={handlePageSizeChange}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                showTotal={(total) => `${total} keys`}
              />
            )}
          </div>
        </>
      )}

      {/* PER-UNIT OFFSET GRID */}
      {!hideOffsetGrid && unitBasedCategories.length > 0 && units > 0 && (
        <>
          <Divider orientation="left" plain>
            Per-Unit Offsets
          </Divider>
          {overlappingCells.size > 0 && (
            <div
              style={{
                color: "#ff4d4f",
                marginBottom: 8,
                fontWeight: 500,
              }}
            >
              Overlapping byte ranges detected — resolve before saving.
            </div>
          )}
          <Table
            dataSource={offsetTableData}
            columns={offsetColumns}
            pagination={false}
            size="small"
            bordered
            locale={{ emptyText: "No units configured" }}
          />
          {showAddUnit && (
            <div style={{ textAlign: "center", marginTop: 8, marginBottom: 8 }}>
              <Tooltip title="Add another unit (auto-fills from pattern)">
                <Button
                  type="dashed"
                  onClick={handleAddUnit}
                  icon={<FontAwesomeIcon icon={faPlus} />}
                >
                  {" "}
                  Add Unit
                </Button>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </div>
  );
}
